import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, Legend,
} from 'recharts';
import { useTwin } from '../context/TwinContext';
import { ewmaSimStore, SENSOR_BASELINE } from '../utils/ewmaSimStore';
import './MLPredictions.css';

const STAGE_COLOR: Record<string, string> = {
  NORMAL: '#34d399', EARLY: '#60a5fa', MID: '#fbbf24', LATE: '#f87171',
};
const STAGE_LABEL: Record<string, string> = {
  NORMAL: '✅ Normal', EARLY: '🔵 Early', MID: '🟡 Medium', LATE: '🔴 Critical',
};

const SLIDERS = [
  { key: 'temp'  as const, label: 'Temperature', unit: '°C',  icon: '🌡️', color: '#f87171' },
  { key: 'vib'   as const, label: 'Vibration',   unit: 'g',   icon: '📳', color: '#f97316' },
  { key: 'speed' as const, label: 'Speed',       unit: 'RPM', icon: '⚙️', color: '#a78bfa' },
];

const RANGE_KEY = 'sliderRanges';
const defaultRanges = { temp: { min: 0, max: 150 }, vib: { min: 0, max: 15 }, speed: { min: 0, max: 200 } };

// ── Star burst dot for threshold crossings ───────────────────
function StarDot(props: any) {
  const { cx, cy, value, payload, earlyZone, ucl2, ucl3 } = props;
  if (!cx || !cy || !payload) return null;

  // Check if this point is a crossing point
  const prev = payload.prev;
  const crossedEarly  = prev !== undefined && prev < earlyZone && value >= earlyZone;
  const crossedMid    = prev !== undefined && prev < ucl2      && value >= ucl2;
  const crossedLate   = prev !== undefined && prev < ucl3      && value >= ucl3;

  if (!crossedEarly && !crossedMid && !crossedLate) return null;

  const color = crossedLate ? '#f87171' : crossedMid ? '#f59e0b' : '#60a5fa';
  const size  = crossedLate ? 18 : crossedMid ? 16 : 14;

  // Draw a star shape using SVG polygon
  const star = (cx: number, cy: number, r: number, ir: number, pts: number) => {
    const points = [];
    for (let i = 0; i < pts * 2; i++) {
      const angle = (i * Math.PI) / pts - Math.PI / 2;
      const radius = i % 2 === 0 ? r : ir;
      points.push(`${cx + radius * Math.cos(angle)},${cy + radius * Math.sin(angle)}`);
    }
    return points.join(' ');
  };

  return (
    <g>
      {/* Glow ring */}
      <circle cx={cx} cy={cy} r={size + 4} fill={color} opacity={0.2} />
      <circle cx={cx} cy={cy} r={size + 2} fill={color} opacity={0.15} />
      {/* Star */}
      <polygon
        points={star(cx, cy, size / 2, size / 4, 5)}
        fill={color}
        stroke="#fff"
        strokeWidth={1.5}
        opacity={0.95}
      />
      {/* Center dot */}
      <circle cx={cx} cy={cy} r={3} fill="#fff" />
    </g>
  );
}

// ── Per-sensor EWMA chart ──────────────────────────────────────
function SensorChart({ label, unit, data, stage, mu, sigma, slope, variance, ct, ttf }: {
  label: string; unit: string;
  data: { t: number; raw: number; ewma: number; ucl2: number; ucl3: number }[];
  stage: string; mu: number; sigma: number;
  slope: number; variance: number; ct: number; ttf: number;
}) {
  const stageColor = STAGE_COLOR[stage] || '#34d399';
  const ucl2 = data[data.length - 1]?.ucl2 || 0;
  const ucl3 = data[data.length - 1]?.ucl3 || 0;
  const earlyZone = mu + sigma * 1.5;

  // Enrich data with prev ewma for crossing detection
  const enriched = data.map((d, i) => ({
    ...d,
    prev: i > 0 ? data[i - 1].ewma : undefined,
  }));

  // Only show last 20 cycles
  const visibleData = enriched.slice(-20);

  // Spread zone lines far apart visually by forcing Y domain
  const earlyLine  = earlyZone;
  const mediumLine = ucl2;
  const critLine   = ucl3;
  const yMin = Math.min(...visibleData.map(d => d.ewma), earlyLine) * 0.85;
  const yMax = Math.max(...visibleData.map(d => d.ewma), critLine)  * 1.25;

  return (
    <div style={{ marginBottom: 20, borderRadius: 12, overflow: 'hidden',
      border: `2px solid ${stageColor}`, boxShadow: `0 4px 20px ${stageColor}22` }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 16px', background: `linear-gradient(135deg, ${stageColor}18, ${stageColor}08)`,
        borderBottom: `1px solid ${stageColor}33` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: stageColor,
            boxShadow: `0 0 8px ${stageColor}` }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: '#e5e7eb' }}>{label}</span>
          <span style={{ fontSize: 11, color: '#9ca3af' }}>({unit})</span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: stageColor,
          padding: '3px 12px', borderRadius: 20, background: `${stageColor}18`,
          border: `1.5px solid ${stageColor}` }}>
          {STAGE_LABEL[stage]}
        </span>
      </div>
      {/* Chart */}
      <div style={{ background: '#fff', padding: '8px 4px 4px' }}>
        {data.length < 3 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '40px 0' }}>
            {data.length === 0 ? '▶ Press Start to begin simulation' : '⏳ Collecting data...'}
          </div>
        ) : (<>
          <ResponsiveContainer width="100%" aspect={1.6}>
            <LineChart data={visibleData} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
              <defs>
                <linearGradient id={`grad-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={stageColor} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={stageColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="t" stroke="#d1d5db" tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#e5e7eb' }} tickLine={false}
                label={{ value: 'Simulation Cycle', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#000000', fontWeight: 700 }} />
              <YAxis stroke="#d1d5db" tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={{ stroke: '#e5e7eb' }} tickLine={false} width={50}
                domain={[yMin, yMax]}
                label={{ value: `${label} ${unit}`, angle: -90, position: 'insideLeft', offset: 10, fontSize: 11, fill: '#000000', fontWeight: 700 }} />
              <Tooltip
                contentStyle={{ background: '#fff', border: `1.5px solid ${stageColor}`, borderRadius: 8,
                  fontSize: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', color: '#111827' }}
                formatter={(v: any) => [`${(+v).toFixed(3)} ${unit}`]}
                labelFormatter={(l) => `Cycle ${l}`}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8, color: '#6b7280' }} />
              {/* Zone fills */}
              {critLine   > 0 && <ReferenceLine y={critLine}   stroke="#f87171" strokeWidth={2} strokeDasharray="6 3"
                label={{ value: '🔴 Critical', fill: '#f87171', fontSize: 10, fontWeight: 700, position: 'insideTopRight' }} />}
              {mediumLine > 0 && <ReferenceLine y={mediumLine} stroke="#f59e0b" strokeWidth={2} strokeDasharray="6 3"
                label={{ value: '🟡 Medium',   fill: '#f59e0b', fontSize: 10, fontWeight: 700, position: 'insideTopRight' }} />}
              {earlyLine  > 0 && <ReferenceLine y={earlyLine}  stroke="#60a5fa" strokeWidth={2} strokeDasharray="4 4"
                label={{ value: '🔵 Early',    fill: '#60a5fa', fontSize: 10, fontWeight: 700, position: 'insideTopRight' }} />}
              <Line type="monotone" dataKey="raw" stroke="#94a3b8" strokeWidth={1.5}
                dot={false} name="Raw" />
              <Line type="monotone" dataKey="ewma" stroke={stageColor} strokeWidth={3}
                dot={(props: any) => <StarDot {...props} earlyZone={earlyLine} ucl2={mediumLine} ucl3={critLine} />}
                name="EWMA" activeDot={{ r: 5, fill: stageColor, stroke: '#fff', strokeWidth: 2 }}
                strokeDasharray={stage === 'NORMAL' ? '6 3' : undefined} />
            </LineChart>
          </ResponsiveContainer>
          {/* Live indicators */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, padding: '10px 12px 8px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
            {[
              { label: 'EWMA', value: data[data.length-1]?.ewma.toFixed(3) ?? '—', unit, color: stageColor },
              { label: 'Slope', value: slope !== undefined ? (slope > 0 ? '+' : '') + slope.toFixed(5) : '—', unit: '/cycle',
                color: slope > 0.001 ? '#f87171' : slope > 0 ? '#f59e0b' : '#34d399' },
              { label: 'Variance', value: variance !== undefined ? variance.toFixed(4) : '—', unit: '',
                color: variance > 0.05 ? '#f87171' : '#34d399' },
              { label: 'TTF', value: ttf > 0 ? ttf.toString() : '∞', unit: ttf > 0 ? ' cycles' : '',
                color: ttf > 0 && ttf < 100 ? '#f87171' : ttf > 0 && ttf < 300 ? '#f59e0b' : '#34d399' },
            ].map(ind => (
              <div key={ind.label} style={{ textAlign: 'center', padding: '6px 4px', borderRadius: 6, background: '#fff', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2, fontWeight: 600, textTransform: 'uppercase' }}>{ind.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: ind.color }}>{ind.value}<span style={{ fontSize: 9, color: '#9ca3af' }}>{ind.unit}</span></div>
              </div>
            ))}
          </div>
          {/* CT indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px 10px', background: '#f9fafb' }}>
            <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>CYCLE TIME:</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: ct > 2.10 ? '#f87171' : ct > 2.06 ? '#f59e0b' : '#34d399' }}>
              {ct.toFixed(3)}s
            </span>
            <span style={{ fontSize: 10, color: '#9ca3af' }}>(baseline 2.0s)</span>
            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, marginLeft: 4,
              background: ct > 2.10 ? '#fef2f2' : ct > 2.06 ? '#fffbeb' : '#f0fdf4',
              color: ct > 2.10 ? '#f87171' : ct > 2.06 ? '#f59e0b' : '#34d399',
              border: `1px solid ${ct > 2.10 ? '#fecaca' : ct > 2.06 ? '#fde68a' : '#bbf7d0'}` }}>
              {ct > 2.10 ? '🔴 Late gate' : ct > 2.06 ? '🟡 Early gate' : '✅ Normal'}
            </span>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ── What-If Simulator ──────────────────────────────────────────
function WhatIfSimulator({ machine, onClose }: { machine: any; onClose: () => void }) {
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const unsub = ewmaSimStore.subscribe(() => forceUpdate(n => n + 1));
    return () => unsub();
  }, []);

  const sim = ewmaSimStore.get();
  const isThisMachine = sim?.machineId === machine.machineId;

  const ranges = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(RANGE_KEY) || 'null') || defaultRanges; }
    catch { return defaultRanges; }
  }, []);

  // Local slider values — synced from store if running
  const [values, setValues] = useState(() =>
    (isThisMachine && sim) ? sim.values : { temp: 0, vib: 0, speed: 0 }
  );

  // Sync slider changes to store while running
  const handleSliderChange = useCallback((key: string, val: number) => {
    const newValues = { ...values, [key]: val };
    setValues(newValues);
    if (isThisMachine && sim?.running) {
      ewmaSimStore.setValues(newValues);
    }
  }, [values, isThisMachine, sim]);

  const start = useCallback(() => {
    ewmaSimStore.start(machine.machineId, machine.name, values, ranges);
  }, [machine, values, ranges]);

  const pause  = useCallback(() => ewmaSimStore.pause(), []);
  const resume = useCallback(() => ewmaSimStore.resume(), []);
  const reset  = useCallback(() => { ewmaSimStore.stop(); setValues({ temp: 0, vib: 0, speed: 0 }); }, []);

  const running = isThisMachine ? (sim?.running ?? false) : false;
  const cycle   = isThisMachine ? (sim?.cycle ?? 0) : 0;
  const sensors = isThisMachine ? (sim?.sensors ?? {}) : {};

  const worstStage = useMemo(() => {
    const rank: Record<string, number> = { NORMAL: 0, EARLY: 1, MID: 2, LATE: 3 };
    return Object.values(sensors).reduce((worst: string, s: any) =>
      (rank[s.stage] ?? 0) > (rank[worst] ?? 0) ? s.stage : worst, 'NORMAL');
  }, [sensors]);

  return (
    <div className="whatif-panel card">
      <div className="whatif-header">
        <div>
          <h3>🧪 What-If Simulator</h3>
          <span className="whatif-machine">{machine.name}</span>
          {isThisMachine && sim && (
            <span style={{ fontSize: 11, color: '#34d399', marginLeft: 8 }}>
              ● Running in background
            </span>
          )}
        </div>
        <div className="whatif-actions">
          <button className="whatif-reset" onClick={reset}>↺ Reset</button>
          <button className="whatif-close" onClick={onClose}>✕</button>
        </div>
      </div>

      <div className="whatif-body">
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {running ? 'Drag sliders — EWMA responds live' : 'Set Starting Values'}
        </div>
        <div className="whatif-sliders">
          {SLIDERS.map(sl => {
            const val = values[sl.key];
            const pct = ((val - ranges[sl.key].min) / (ranges[sl.key].max - ranges[sl.key].min)) * 100;
            return (
              <div key={sl.key} className="whatif-slider-row">
                <div className="ws-label">
                  <span>{sl.icon} {sl.label}</span>
                  <span className="ws-val" style={{ color: sl.color }}>
                    {sl.key === 'vib' ? val.toFixed(1) : val.toFixed(0)}{sl.unit}
                  </span>
                </div>
                <div className="ws-track">
                  <input type="range" min={ranges[sl.key].min} max={ranges[sl.key].max}
                    step={sl.key === 'vib' ? 0.1 : 1} value={val}
                    onChange={e => handleSliderChange(sl.key, +e.target.value)}
                    className="ws-range"
                    style={{ '--fill-pct': `${pct}%`, '--fill-color': sl.color } as React.CSSProperties}
                  />
                  <div className="ws-range-labels">
                    <span>{ranges[sl.key].min}</span>
                    <span>{ranges[sl.key].max}{sl.unit}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center' }}>
          {!isThisMachine || !sim ? (
            <button onClick={start} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none',
              background: '#34d399', color: '#0a0e1a', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              ▶ Start EWMA Simulation
            </button>
          ) : running ? (
            <button onClick={pause} style={{ flex: 1, padding: '10px', borderRadius: 8,
              border: '1px solid #f87171', background: 'transparent', color: '#f87171', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              ⏸ Pause
            </button>
          ) : (
            <button onClick={resume} style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none',
              background: '#34d399', color: '#0a0e1a', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              ▶ Resume
            </button>
          )}
          {cycle > 0 && <span style={{ fontSize: 12, color: '#6b7280' }}>Cycle {cycle} · {cycle * 2}s</span>}
        </div>

        {cycle > 0 && (
          <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, textAlign: 'center',
            background: `${STAGE_COLOR[worstStage]}18`, border: `1px solid ${STAGE_COLOR[worstStage]}44` }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: STAGE_COLOR[worstStage] }}>
              Overall: {STAGE_LABEL[worstStage]}
            </span>
          </div>
        )}
      </div>

      {cycle > 0 && (
        <div style={{ padding: '16px 0 0' }}>
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 12, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            📊 Live EWMA Charts
          </div>
          {SLIDERS.map(sl => {
            const bl = SENSOR_BASELINE[sl.key];
            return (
              <SensorChart
                key={sl.key}
                label={sl.label}
                unit={sl.unit}
                data={sensors[sl.key]?.history || []}
                stage={sensors[sl.key]?.stage || 'NORMAL'}
                mu={bl.mu}
                sigma={bl.sigma}
                slope={sensors[sl.key]?.slope ?? 0}
                variance={sensors[sl.key]?.variance ?? 0}
                ct={sensors[sl.key]?.ct ?? 2.0}
                ttf={sensors[sl.key]?.ttf ?? -1}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────
const MLPredictions: React.FC = () => {
  const { getActiveTwin } = useTwin();
  const activeTwin = getActiveTwin();
  const [whatIfMachineId, setWhatIfMachineId] = useState<string | null>(null);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const unsub = ewmaSimStore.subscribe(() => forceUpdate(n => n + 1));
    return () => { unsub(); };
  }, []);

  const sim = ewmaSimStore.get();

  // Auto-open simulator if one is already running
  useEffect(() => {
    if (sim && !whatIfMachineId) {
      setWhatIfMachineId(sim.machineId);
    }
  }, []);

  const machines = useMemo(() => activeTwin?.machines || [], [activeTwin]);

  if (!activeTwin) {
    return (
      <div className="ml-page">
        <div className="ml-empty">
          <div className="empty-icon">🤖</div>
          <h2>ML Predictive Maintenance</h2>
          <p>No active twin selected. Create a digital twin to see AI-powered failure predictions.</p>
        </div>
      </div>
    );
  }

  if (!machines.length) {
    return (
      <div className="ml-page">
        <div className="page-header"><h1>🤖 ML Predictive Maintenance</h1></div>
        <div className="ml-empty">
          <div className="empty-icon">⚙️</div>
          <h2>No Machines</h2>
          <p>Add machines to your twin to generate failure predictions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ml-page">
      <div className="page-header">
        <h1>🤖 ML Predictive Maintenance</h1>
        <div className="header-right">
          {sim && (
            <span style={{ fontSize: 12, color: '#34d399', background: 'rgba(52,211,153,0.1)',
              border: '1px solid rgba(52,211,153,0.3)', borderRadius: 6, padding: '3px 10px', marginRight: 8 }}>
              ● Simulation running
            </span>
          )}
          <button className="btn-refresh"
            style={{ background: 'rgba(16,185,129,0.15)', color: '#34d399', borderColor: 'rgba(16,185,129,0.3)', fontWeight: 700, fontSize: 14, padding: '6px 18px' }}
            onClick={() => { const m = machines[0]; if (m) setWhatIfMachineId(m.machineId); }}>
            🧪 What-If Simulator
          </button>
        </div>
      </div>

      <div className="kpi-row">
        {[
          { label: 'Machines', value: machines.length,                                           icon: '⚙️', color: '#a78bfa' },
          { label: 'Running',  value: machines.filter((m: any) => m.status === 'running').length, icon: '🟢', color: '#34d399' },
          { label: 'Fault',    value: machines.filter((m: any) => m.status === 'fault').length,   icon: '🔴', color: '#f87171' },
          { label: 'Sensors',  value: machines.reduce((n: number, m: any) => n + (m.sensors?.length || 0), 0), icon: '📡', color: '#00d4ff' },
        ].map((k, i) => (
          <div className="kpi-card" key={i}>
            <span className="kpi-icon">{k.icon}</span>
            <span className="kpi-value" style={{ color: k.color }}>{k.value}</span>
            <span className="kpi-label">{k.label}</span>
          </div>
        ))}
      </div>

      {whatIfMachineId && (() => {
        const m = machines.find((m: any) => m.machineId === whatIfMachineId);
        return m ? <WhatIfSimulator machine={m} onClose={() => setWhatIfMachineId(null)} /> : null;
      })()}

      <div className="ml-grid">
        <div className="ml-col-left">
          <div className="card ml-timeline-card">
            <h3>⚙️ Machines</h3>
            <p className="card-subtitle">Click to open What-If Simulator</p>
            <div className="timeline">
              {machines.map((m: any) => (
                <div key={m.machineId}
                  className={`timeline-item ${whatIfMachineId === m.machineId ? 'tl-selected' : ''}`}
                  onClick={() => setWhatIfMachineId(m.machineId)}
                  style={{ cursor: 'pointer' }}>
                  <div className="tl-dot" style={{
                    background: m.status === 'fault' ? '#f87171' : m.status === 'idle' ? '#6b7280' : '#34d399',
                    boxShadow: `0 0 6px ${m.status === 'fault' ? '#f87171' : m.status === 'idle' ? '#6b7280' : '#34d399'}`,
                  }} />
                  <div className="tl-content">
                    <div className="tl-header">
                      <span className="tl-name">{m.name}</span>
                      <span className="tl-badge" style={{
                        color: m.status === 'fault' ? '#f87171' : m.status === 'idle' ? '#6b7280' : '#34d399',
                        borderColor: m.status === 'fault' ? '#f87171' : m.status === 'idle' ? '#6b7280' : '#34d399',
                      }}>{m.status?.toUpperCase()}</span>
                    </div>
                    <div className="tl-meta"><span>{m.sensors?.length || 0} sensors</span></div>
                  </div>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>🧪</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="ml-col-right">
          <div className="card ml-detail-card">
            <h3 style={{ margin: 0, fontSize: 16, color: '#e5e7eb', marginBottom: 12 }}>🧪 What-If Simulation</h3>
            {!whatIfMachineId ? (
              <div style={{ textAlign: 'center', padding: '30px 0' }}>
                <div style={{ fontSize: 44, marginBottom: 14 }}>🧪</div>
                <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 12 }}>No simulation running</div>
                <div style={{ textAlign: 'left', padding: '14px 16px', borderRadius: 10,
                  background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)' }}>
                  <div style={{ fontSize: 11, color: '#a78bfa', fontWeight: 700, marginBottom: 8,
                    textTransform: 'uppercase', letterSpacing: '0.5px' }}>How It Works</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.8 }}>
                    1️⃣ Click a machine from the list<br />
                    2️⃣ Set starting values with sliders<br />
                    3️⃣ Press ▶ Start EWMA Simulation<br />
                    4️⃣ Navigate away — simulation keeps running!<br />
                    5️⃣ Alerts fire on Alerts page + email
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Simulator is open on the left panel.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MLPredictions;
